import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ChevronRight, Calendar, MapPin, Star } from "lucide-react";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { api } from "../lib/api";

const BLOG_FALLBACK = "https://images.unsplash.com/photo-1495020689067-958852a7765e?auto=format&fit=crop&w=1200&q=80";
import { useAuth } from "../lib/auth";

const FALLBACK_DOCTOR_IMG =
  "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?auto=format&fit=crop&w=400&q=80";

function Section({ title, subtitle, action, children, testId }) {
  return (
    <section className="space-y-4" data-testid={testId}>
      <div className="flex items-end justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">{title}</h2>
          {subtitle && <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function DoctorMiniCard({ d, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`featured-doctor-${d.id}`}
      className="text-left bg-white border border-border rounded-2xl p-4 shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5"
    >
      <div className="flex items-center gap-3">
        <img
          src={d.image_url || FALLBACK_DOCTOR_IMG}
          alt={d.name}
          className="h-14 w-14 rounded-xl object-cover"
        />
        <div className="min-w-0 flex-1">
          <div className="font-semibold truncate">{d.name}</div>
          <div className="text-xs text-muted-foreground truncate">{d.category}</div>
          <div className="flex items-center gap-2 mt-1 text-xs">
            <span className="inline-flex items-center gap-1 text-amber-600">
              <Star className="h-3.5 w-3.5 fill-current" /> {d.rating?.toFixed?.(1) ?? d.rating}
            </span>
            <span className="text-muted-foreground">·</span>
            <span className="text-foreground font-medium">₹{d.consultation_fee}</span>
          </div>
        </div>
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
      </div>
    </button>
  );
}

export default function PatientDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [nextAppt, setNextAppt] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [catsRes, docsRes, clinicsRes, apptsRes] = await Promise.all([
          api.get("/categories"),
          api.get("/doctors?sort=rating&limit=3"),
          api.get("/clinics"),
          api.get("/appointments/mine?status=upcoming"),
        ]);
        if (cancelled) return;
        setCategories(catsRes.data || []);
        setFeatured(docsRes.data.items || []);
        setClinics(clinicsRes.data || []);
        setNextAppt(apptsRes.data.items?.[0] || null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const [banners, setBanners] = useState([]);
  const [bannerIdx, setBannerIdx] = useState(0);
  const [blogs, setBlogs] = useState([]);

  useEffect(() => {
    api.get("/banners?position=home_top").then((r) => setBanners(r.data.items || [])).catch(() => {});
    api.get("/blogs?page=1&page_size=3").then((r) => setBlogs(r.data.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (banners.length <= 1) return;
    const t = setInterval(() => setBannerIdx((i) => (i + 1) % banners.length), 6000);
    return () => clearInterval(t);
  }, [banners.length]);

  const submitSearch = (e) => {
    e?.preventDefault?.();
    const trimmed = q.trim();
    navigate(`/patient/doctors${trimmed ? `?q=${encodeURIComponent(trimmed)}` : ""}`);
  };

  const formatScheduled = (iso) => {
    if (!iso) return "";
    const dt = new Date(iso);
    return dt.toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: "Asia/Kolkata",
    });
  };

  const displayName = user?.name || user?.phone || "there";

  return (
    <div className="min-h-screen bg-card text-foreground flex flex-col">
      <Navbar />
      <main
        data-testid="patient-dashboard"
        className="flex-1 px-6 lg:px-10 py-8 lg:py-10 max-w-7xl w-full mx-auto space-y-10"
      >
        {/* Hero banners */}
        {banners.length > 0 && (
          <section data-testid="patient-dashboard-banner" className="relative rounded-3xl overflow-hidden shadow-sm">
            {banners.map((b, i) => (
              <Link
                key={b.id}
                to={b.link || "#"}
                data-testid={`banner-slide-${b.id}`}
                className={"block transition-opacity duration-700 " + (i === bannerIdx ? "opacity-100" : "opacity-0 absolute inset-0 pointer-events-none")}
              >
                <div className="relative h-44 sm:h-56 lg:h-64">
                  <img src={b.image_url} alt={b.title} className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent" />
                  <div className="relative h-full flex items-end p-6 sm:p-8">
                    <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight text-white max-w-xl">{b.title}</h2>
                  </div>
                </div>
              </Link>
            ))}
            {banners.length > 1 && (
              <div className="absolute bottom-3 right-3 flex gap-1.5">
                {banners.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    aria-label={`Show banner ${i + 1}`}
                    data-testid={`banner-dot-${i}`}
                    onClick={() => setBannerIdx(i)}
                    className={"h-2 w-2 rounded-full transition-all " + (i === bannerIdx ? "bg-white w-6" : "bg-white/50")}
                  />
                ))}
              </div>
            )}
          </section>
        )}
        {/* Welcome */}
        <section
          data-testid="patient-dashboard-welcome-card"
          className="bg-primary text-primary-foreground rounded-3xl p-7 sm:p-9 relative overflow-hidden shadow-sm"
        >
          <div className="absolute -right-12 -top-12 h-56 w-56 rounded-full bg-white/10 blur-2xl" aria-hidden />
          <div className="absolute -left-6 -bottom-12 h-44 w-44 rounded-full bg-white/10 blur-3xl" aria-hidden />
          <div className="relative">
            <div className="text-xs font-bold uppercase tracking-[0.2em] opacity-80 mb-3">
              Patient · Healthink
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              Hi {displayName.split(" ")[0]}, how can we help?
            </h1>
            <p className="mt-2 text-sm sm:text-base opacity-90 max-w-xl">
              Search verified specialists, book a video consult, or walk into a partner clinic.
            </p>

            <form onSubmit={submitSearch} className="mt-6 relative max-w-xl">
              <Search className="h-5 w-5 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="patient-dashboard-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search 'cardio', 'skin', 'pediatrician'…"
                className="h-14 pl-12 pr-32 bg-white text-foreground border-0 rounded-2xl text-base"
              />
              <Button
                type="submit"
                data-testid="patient-dashboard-search-btn"
                className="absolute right-2 top-1/2 -translate-y-1/2 h-10 rounded-xl px-5 bg-foreground text-background hover:bg-foreground/90 hover:text-background"
              >
                Search
              </Button>
            </form>
          </div>
        </section>

        {/* Upcoming appointment preview */}
        {nextAppt && (
          <Section
            title="Your next appointment"
            subtitle="Don't be late — join 5 minutes early."
            testId="patient-dashboard-next-appt"
            action={
              <Link
                to="/patient/appointments"
                data-testid="patient-dashboard-view-all-appts"
                className="text-sm font-medium text-primary hover:underline"
              >
                View all →
              </Link>
            }
          >
            <Link
              to={`/patient/appointments/${nextAppt.id}`}
              data-testid={`patient-dashboard-next-appt-card-${nextAppt.id}`}
              className="block bg-white border border-border rounded-2xl p-5 shadow-sm hover:shadow-md transition-all"
            >
              <div className="flex items-center gap-4">
                <img
                  src={nextAppt.doctor?.image_url || FALLBACK_DOCTOR_IMG}
                  alt={nextAppt.doctor?.name}
                  className="h-14 w-14 rounded-xl object-cover"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{nextAppt.doctor?.name}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {nextAppt.doctor?.category}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                    <span className="inline-flex items-center gap-1 text-foreground">
                      <Calendar className="h-3.5 w-3.5" />
                      {formatScheduled(nextAppt.scheduled_at)}
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-accent text-accent-foreground font-medium uppercase tracking-wide">
                      {nextAppt.mode}
                    </span>
                  </div>
                </div>
                <span className="text-xs font-medium text-primary inline-flex items-center gap-1">
                  Details <ChevronRight className="h-3.5 w-3.5" />
                </span>
              </div>
            </Link>
          </Section>
        )}

        {/* Categories */}
        <Section title="Browse by specialty" testId="patient-dashboard-categories">
          {loading ? (
            <div className="flex gap-3 overflow-x-auto pb-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-12 w-32 bg-muted rounded-full animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-2 px-2">
              {categories.map((c) => (
                <button
                  key={c.id}
                  data-testid={`category-chip-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => navigate(`/patient/doctors?category=${encodeURIComponent(c.name)}`)}
                  className="shrink-0 px-5 h-12 rounded-full border border-border bg-white hover:bg-secondary hover:border-primary/40 transition-all text-sm font-medium"
                >
                  {c.name}
                </button>
              ))}
            </div>
          )}
        </Section>

        {/* Featured doctors */}
        <Section
          title="Top-rated doctors"
          subtitle="Our highest-reviewed specialists this week."
          testId="patient-dashboard-featured-doctors"
          action={
            <Link to="/patient/doctors" data-testid="patient-dashboard-see-all-doctors" className="text-sm font-medium text-primary hover:underline">
              See all →
            </Link>
          }
        >
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-24 bg-muted rounded-2xl animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {featured.map((d) => (
                <DoctorMiniCard
                  key={d.id}
                  d={d}
                  onClick={() => navigate(`/patient/doctors/${d.id}`)}
                />
              ))}
            </div>
          )}
        </Section>

        {/* Nearby clinics */}
        <Section
          title="Partner clinics near you"
          subtitle="Walk-in OPD slots at trusted clinics."
          testId="patient-dashboard-clinics"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {clinics.map((c) => (
              <div
                key={c.id}
                data-testid={`clinic-card-${c.id}`}
                className="bg-white border border-border rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all"
              >
                <div className="h-32 w-full relative overflow-hidden">
                  <img src={c.image_url} alt={c.name} className="h-full w-full object-cover" />
                </div>
                <div className="p-4">
                  <div className="font-semibold">{c.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5 inline-flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5" /> {c.city}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* Health Articles */}
        {blogs.length > 0 && (
          <Section
            title="Health Articles"
            subtitle="Evidence-based tips from our medical editors."
            testId="patient-dashboard-blogs"
            action={
              <Link to="/patient/blogs" data-testid="patient-dashboard-view-all-blogs" className="text-sm font-medium text-primary hover:underline">
                View all →
              </Link>
            }
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {blogs.map((b) => (
                <Link key={b.id} to={`/patient/blogs/${b.slug}`} data-testid={`patient-dashboard-blog-${b.slug}`} className="group bg-white border border-border rounded-2xl overflow-hidden shadow-sm hover:shadow-md hover:-translate-y-1 transition-all">
                  <img
                    src={b.cover_image_url || BLOG_FALLBACK}
                    alt=""
                    onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = BLOG_FALLBACK; }}
                    className="w-full h-40 object-cover"
                  />
                  <div className="p-4">
                    <h3 className="font-semibold tracking-tight line-clamp-2 group-hover:text-primary transition-colors">{b.title}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{b.excerpt}</p>
                    <div className="mt-2 text-xs font-medium text-primary">Read more →</div>
                  </div>
                </Link>
              ))}
            </div>
          </Section>
        )}
      </main>
    </div>
  );
}
